import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ArrowRight, HeartPulse, Car, ShieldCheck, Landmark, Scale, Briefcase, HandHeart, Leaf, GraduationCap, X } from "lucide-react";

export default function Themes() {
  const { t } = useTranslation("landing");
  const [activeThemeIndex, setActiveThemeIndex] = useState<number | null>(null);

  const themes = [
    { icon: HeartPulse, title: t("themes.list.health.title", "AI + Healthcare"), body: t("themes.list.health.description", "AI is transforming healthcare..."), color: "rose", key: "health" },
    { icon: Car, title: t("themes.list.transportation.title", "AI + Transportation"), body: t("themes.list.transportation.description", "AI improves transportation..."), color: "blue", key: "transportation" },
    { icon: ShieldCheck, title: t("themes.list.cybersecurity.title", "AI + Cybersecurity"), body: t("themes.list.cybersecurity.description", "AI strengthens cybersecurity..."), color: "indigo", key: "cybersecurity" },
    { icon: Landmark, title: t("themes.list.finance.title", "AI + Finance"), body: t("themes.list.finance.description", "AI helps analyze massive amounts..."), color: "emerald", key: "finance" },
    { icon: Scale, title: t("themes.list.law.title", "AI + Law"), body: t("themes.list.law.description", "AI supports legal work..."), color: "amber", key: "law" },
    { icon: Briefcase, title: t("themes.list.business.title", "AI + Business"), body: t("themes.list.business.description", "AI helps businesses understand..."), color: "violet", key: "business" },
    { icon: HandHeart, title: t("themes.list.social.title", "AI + Social Good"), body: t("themes.list.social.description", "Using AI to solve global challenges..."), color: "pink", key: "social" },
    { icon: Leaf, title: t("themes.list.environment.title", "AI + Environment"), body: t("themes.list.environment.description", "AI for sustainability..."), color: "teal", key: "environment" },
    { icon: GraduationCap, title: t("themes.list.education.title", "AI + Education"), body: t("themes.list.education.description", "Personalizing learning with AI..."), color: "sky", key: "education" },
  ] as const;

  const colorMap: Record<string, { bg: string; text: string; border: string; accent: string; headerBg: string }> = {
    rose: {
      bg: "bg-rose-50",
      text: "text-rose-600",
      border: "border-rose-100",
      accent: "#E11D48",
      headerBg: "bg-gradient-to-r from-rose-600 to-rose-500",
    },
    blue: {
      bg: "bg-blue-50",
      text: "text-blue-600",
      border: "border-blue-100",
      accent: "#2563EB",
      headerBg: "bg-gradient-to-r from-blue-600 to-blue-500",
    },
    indigo: {
      bg: "bg-indigo-50",
      text: "text-indigo-600",
      border: "border-indigo-100",
      accent: "#4F46E5",
      headerBg: "bg-gradient-to-r from-indigo-600 to-indigo-500",
    },
    emerald: {
      bg: "bg-emerald-50",
      text: "text-emerald-600",
      border: "border-emerald-100",
      accent: "#059669",
      headerBg: "bg-gradient-to-r from-emerald-600 to-emerald-500",
    },
    amber: {
      bg: "bg-amber-50",
      text: "text-amber-600",
      border: "border-amber-100",
      accent: "#D97706",
      headerBg: "bg-gradient-to-r from-amber-600 to-amber-500",
    },
    violet: {
      bg: "bg-violet-50",
      text: "text-violet-600",
      border: "border-violet-100",
      accent: "#7C3AED",
      headerBg: "bg-gradient-to-r from-violet-600 to-violet-500",
    },
    pink: {
      bg: "bg-pink-50",
      text: "text-pink-600",
      border: "border-pink-100",
      accent: "#DB2777",
      headerBg: "bg-gradient-to-r from-pink-600 to-pink-500",
    },
    teal: {
      bg: "bg-teal-50",
      text: "text-teal-600",
      border: "border-teal-100",
      accent: "#0D9488",
      headerBg: "bg-gradient-to-r from-teal-600 to-teal-500",
    },
    sky: {
      bg: "bg-sky-50",
      text: "text-sky-600",
      border: "border-sky-100",
      accent: "#0284C7",
      headerBg: "bg-gradient-to-r from-sky-600 to-sky-500",
    },
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const getRoadmapSteps = (themeKey: string) => [
    {
      number: 1,
      title: t("themes.steps.step1.title", "Discover the Landscape"),
      desc: t("themes.steps.step1.desc", "Explore how AI is already changing this field. Understand the real challenges — and why the world needs bold solutions here.")
    },
    {
      number: 2,
      title: t("themes.steps.step2.title", "Get Inside the Data"),
      desc: t(`themes.list.${themeKey}.step2`)
    },
    {
      number: 3,
      title: t("themes.steps.step3.title", "Pick Your Mission"),
      desc: t(`themes.list.${themeKey}.step3`)
    },
    {
      number: 4,
      title: t(`themes.list.${themeKey}.step4Title`),
      desc: t(`themes.list.${themeKey}.step4`)
    },
    {
      number: 5,
      title: t("themes.steps.step5.title", "Test, Break & Improve"),
      desc: t("themes.steps.step5.desc", "Your first model won't be perfect. That's exactly the point. Push it, find its limits, and watch it get smarter.")
    },
    {
      number: 6,
      title: t("themes.steps.step6.title", "Present Your Impact"),
      desc: t("themes.steps.step6.desc", "Stand before an expert jury. Show what you built, who it helps, and what changes in the world because of it.")
    }
  ];

  // Close modal on Escape key press
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setActiveThemeIndex(null);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <section id="themes" className="scroll-mt-20 lg:scroll-mt-24 bg-white py-24">
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center w-full">
          <p className="label-eyebrow">{t("themes.tag")}</p>
          <h2 className="mt-3 text-4xl font-bold text-navy sm:text-[42px]">{t("themes.title")}</h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-body">
            {t("themes.subtitle")}
          </p>
        </div>

        <div className="mt-14 pt-6 w-full grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {themes.map((tItem, i) => (
            <div
              key={tItem.title}
              onClick={() => setActiveThemeIndex(i)}
              className="relative w-full min-h-[220px] rounded-2xl border border-slate-200/80 bg-white p-6 pt-8 shadow-sm hover:shadow-xl hover:border-slate-300 transition-all duration-300 cursor-pointer flex flex-col justify-between group"
            >
              {/* Colored top-left bubble icon */}
              <span className={`absolute -top-6 left-6 h-12 w-12 shrink-0 flex items-center justify-center rounded-full shadow-md transition-transform group-hover:scale-110 duration-300 ${colorMap[tItem.color].bg} ${colorMap[tItem.color].text}`}>
                <tItem.icon className="h-6 w-6" />
              </span>

              <div className="mt-2 flex-grow">
                <h3 className="text-lg font-bold text-navy leading-snug group-hover:text-primary transition-colors">
                  {tItem.title}
                </h3>
                <p className="mt-2 text-xs leading-relaxed text-slate-500 line-clamp-3">
                  {tItem.body}
                </p>
              </div>

              <div className="mt-4 flex items-center gap-1.5 text-xs font-bold text-primary group-hover:translate-x-1 transition-transform duration-300">
                View Roadmap & Details <ArrowRight className="h-3.5 w-3.5" />
              </div>
            </div>
          ))}
        </div>

        {/* <div className="mt-12 text-center">
          <button
            onClick={() => scrollToSection("apply")}
            className="inline-flex items-center gap-2 rounded-full border-2 border-primary bg-transparent px-6 py-3 text-sm font-semibold text-primary transition-colors hover:bg-primary hover:text-white cursor-pointer"
          >
            {t("themes.exploreAll", { defaultValue: "Explore All Themes" })} <ArrowRight className="h-4 w-4" />
          </button>
        </div> */}
      </div>

      {/* Curriculum Roadmap Modal */}
      <AnimatePresence>
        {activeThemeIndex !== null && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setActiveThemeIndex(null)}
              className="absolute inset-0 bg-[#0B061A]/80 backdrop-blur-md"
            />

            {/* Modal Box */}
            <motion.div
              initial={{ opacity: 0, y: 50, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 50, scale: 0.95 }}
              transition={{ duration: 0.3 }}
              className="relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh] z-10 border border-slate-100"
            >
              {/* Header Banner */}
              <div className={`p-8 pr-12 text-white relative ${colorMap[themes[activeThemeIndex].color].headerBg}`}>
                <button
                  onClick={() => setActiveThemeIndex(null)}
                  className="absolute top-6 right-6 p-1.5 rounded-full bg-white/10 hover:bg-white/20 transition-colors text-white border-0 cursor-pointer"
                  aria-label="Close modal"
                >
                  <X className="w-5 h-5" />
                </button>

                <div className="flex items-center gap-3">
                  <span className="p-2 bg-white/10 rounded-xl shrink-0">
                    {React.createElement(themes[activeThemeIndex].icon, { className: "h-6 w-6" })}
                  </span>
                  <h3 className="text-2xl font-bold tracking-wide">
                    {themes[activeThemeIndex].title}
                  </h3>
                </div>
                <p className="text-white/80 text-sm mt-2 italic font-medium">
                  {t(`themes.list.${themes[activeThemeIndex].key}.subtitle`)}
                </p>
              </div>

              {/* Scrollable Curriculum Timeline content */}
              <div className="p-8 overflow-y-auto flex-1 bg-slate-50/30">
                <div className="relative pl-8 border-l-2 border-slate-200/60 ml-3 space-y-8 pb-4">
                  {getRoadmapSteps(themes[activeThemeIndex].key).map((step, idx) => (
                    <div key={idx} className="relative">
                      {/* Timeline circle badge */}
                      <span
                        className="absolute -left-[43px] top-0.5 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white shadow-md transition-all duration-300"
                        style={{ backgroundColor: colorMap[themes[activeThemeIndex].color].accent }}
                      >
                        {step.number}
                      </span>
                      <div>
                        <h4 className="text-sm font-bold text-slate-800 leading-snug">
                          {step.title}
                        </h4>
                        <p className="text-xs text-slate-500 leading-relaxed mt-1">
                          {step.desc}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* You'll Build highlight callout */}
                <div className="mt-8 p-6 rounded-2xl bg-white border border-slate-200/60 shadow-sm flex items-start gap-4">
                  <span className={`p-2 rounded-xl shrink-0 ${colorMap[themes[activeThemeIndex].color].bg} ${colorMap[themes[activeThemeIndex].color].text}`}>
                    <Briefcase className="h-5 w-5" />
                  </span>
                  <div>
                    <h5 className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                      You'll Build
                    </h5>
                    <p className="text-sm font-semibold text-slate-700 mt-1 leading-relaxed">
                      {t(`themes.list.${themes[activeThemeIndex].key}.youllBuild`)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Bottom Apply CTA bar */}
              <div className="p-6 border-t border-slate-100 bg-white flex justify-end gap-3 shrink-0">
                <button
                  onClick={() => setActiveThemeIndex(null)}
                  className="px-5 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-600 font-semibold text-sm hover:bg-slate-50 transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const themeName = themes[activeThemeIndex].title;
                    setActiveThemeIndex(null);
                    scrollToSection("apply");
                    
                    // Pre-fill the form preferred theme if applicable
                    setTimeout(() => {
                      const themeSelect = document.querySelector('select[name="preferredTheme"]') as HTMLSelectElement;
                      if (themeSelect) {
                        for (let opt of themeSelect.options) {
                          if (opt.text.toLowerCase().includes(themeName.toLowerCase()) || themeName.toLowerCase().includes(opt.text.toLowerCase())) {
                            themeSelect.value = opt.value;
                            themeSelect.dispatchEvent(new Event('change', { bubbles: true }));
                            break;
                          }
                        }
                      }
                    }, 500);
                  }}
                  className="px-6 py-2.5 rounded-xl text-white font-bold text-sm shadow-lg transition-all hover:brightness-105 border-0 cursor-pointer"
                  style={{ backgroundColor: colorMap[themes[activeThemeIndex].color].accent }}
                >
                  Apply for this Theme
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </section>
  );
}

